import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { INR, fmtDate, fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";

export default function ClientExport() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/clients/${id}/export`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Could not load export"));
  }, [id]);

  useEffect(() => {
    if (d) { const t = setTimeout(() => window.print(), 500); return () => clearTimeout(t); }
  }, [d]);

  if (err) return <div className="p-10 text-sm text-destructive">{err}</div>;
  if (!d) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;

  const c = d.client;
  const userName = (uid) => d.users.find((u) => u.id === uid)?.name || "—";

  const groupedTasks = { active: [], completed: [] };
  d.tasks.forEach((t) => {
    if (["Completed", "Cancelled"].includes(t.status)) groupedTasks.completed.push(t);
    else groupedTasks.active.push(t);
  });

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { margin: 12mm; size: A4; }
          body { background: white !important; }
          section { page-break-inside: avoid; }
        }
        .print-page { background: white; color: #111; }
        .print-page * { color: #111; border-color: #ddd !important; }
      `}</style>
      <div className="min-h-screen bg-muted/40 py-8 print-page">
        <div className="max-w-4xl mx-auto bg-white border border-border rounded-md p-10 shadow-sm space-y-8" data-testid="client-export-page">
          <div className="no-print flex justify-end gap-2">
            <Button onClick={() => window.print()} className="bg-primary hover:bg-primary/90" data-testid="export-print-btn"><Printer size={14} className="mr-1" />Print / Save PDF</Button>
            <Button variant="outline" onClick={() => window.close()}>Close</Button>
          </div>

          {/* Header */}
          <header className="border-b-2 border-primary pb-6 flex items-start justify-between">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Client 360° Report</div>
              <div className="text-3xl font-black tracking-tight mt-1">{c.company_name}</div>
              <div className="text-sm text-muted-foreground mt-1">{c.client_id} · {[c.city, c.state].filter(Boolean).join(", ")}</div>
            </div>
            <div className="text-right">
              <div className="w-12 h-12 rounded-md bg-primary text-primary-foreground font-black text-xl flex items-center justify-center ml-auto">D</div>
              <div className="mt-2 font-display font-extrabold">Digitalis CRM</div>
              <div className="text-xs text-muted-foreground">Exported {fmtDate(d.exported_at)} by {d.exported_by}</div>
            </div>
          </header>

          {/* Client info + finance */}
          <section>
            <div className="grid grid-cols-2 gap-6 text-sm">
              <div>
                <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Contact</div>
                {c.contact_person && <div><span className="font-mono text-xs text-muted-foreground">Person:</span> {c.contact_person}</div>}
                {c.phone && <div><span className="font-mono text-xs text-muted-foreground">Phone:</span> {c.phone}</div>}
                {c.email && <div><span className="font-mono text-xs text-muted-foreground">Email:</span> {c.email}</div>}
                {c.website && <div><span className="font-mono text-xs text-muted-foreground">Website:</span> {c.website}</div>}
                {c.gst && <div><span className="font-mono text-xs text-muted-foreground">GST:</span> <span className="font-mono">{c.gst}</span></div>}
                {c.industry && <div><span className="font-mono text-xs text-muted-foreground">Industry:</span> {c.industry}</div>}
              </div>
              {d.finance_visible && (
                <div className="border border-border rounded-md p-4 bg-muted/40">
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Financial summary</div>
                  <div className="flex justify-between py-1 text-sm"><span>Total contract value</span><span className="font-mono font-bold">{INR(c.contract_value)}</span></div>
                  <div className="flex justify-between py-1 text-sm"><span>Amount received</span><span className="font-mono text-emerald-700">{INR(c.received)}</span></div>
                  <div className="flex justify-between py-1 text-sm border-t border-border pt-2 mt-1"><span className="font-bold">Outstanding</span><span className="font-mono font-black text-primary">{INR(c.outstanding)}</span></div>
                </div>
              )}
            </div>
          </section>

          {/* Services */}
          <section>
            <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Services opted ({d.services.length})</h2>
            {d.services.length === 0 ? <div className="text-sm text-muted-foreground">No services subscribed.</div> :
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                    <th className="px-2 py-1.5">Service</th>
                    <th className="px-2 py-1.5">Billing</th>
                    <th className="px-2 py-1.5">Start</th>
                    <th className="px-2 py-1.5">Status</th>
                    {d.finance_visible && <th className="px-2 py-1.5 text-right">Quoted</th>}
                    {d.finance_visible && <th className="px-2 py-1.5 text-right">Final</th>}
                  </tr>
                </thead>
                <tbody>
                  {d.services.map((s) => (
                    <tr key={s.id} className="border-t border-border">
                      <td className="px-2 py-1.5 font-semibold">{s.service_name}</td>
                      <td className="px-2 py-1.5 text-xs">{s.billing_type}</td>
                      <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(s.start_date)}</td>
                      <td className="px-2 py-1.5 text-xs">{s.status}</td>
                      {d.finance_visible && <td className="px-2 py-1.5 text-right font-mono">{INR(s.original_quote)}</td>}
                      {d.finance_visible && <td className="px-2 py-1.5 text-right font-mono font-bold">{INR(s.total_contract_value)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </section>

          {/* Projects */}
          <section>
            <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Projects ({d.projects.length})</h2>
            {d.projects.length === 0 ? <div className="text-sm text-muted-foreground">No projects yet.</div> :
              <table className="w-full text-sm">
                <thead className="bg-muted/40"><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                  <th className="px-2 py-1.5">Project</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5">Start</th><th className="px-2 py-1.5">Expected end</th><th className="px-2 py-1.5 text-right">Progress</th>
                </tr></thead>
                <tbody>
                  {d.projects.map((p) => (
                    <tr key={p.id} className="border-t border-border">
                      <td className="px-2 py-1.5 font-semibold">{p.name}</td>
                      <td className="px-2 py-1.5 text-xs">{p.status}</td>
                      <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(p.start_date)}</td>
                      <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(p.expected_end_date)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{p.progress || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </section>

          {/* Deliverables */}
          <section>
            <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Deliverables ({d.deliverables.length})</h2>
            {d.deliverables.length === 0 ? <div className="text-sm text-muted-foreground">No deliverables tracked.</div> :
              <table className="w-full text-sm">
                <thead className="bg-muted/40"><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                  <th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Frequency</th><th className="px-2 py-1.5">Due</th><th className="px-2 py-1.5 text-right">Delivered</th><th className="px-2 py-1.5">Status</th>
                </tr></thead>
                <tbody>
                  {d.deliverables.map((dd) => (
                    <tr key={dd.id} className="border-t border-border">
                      <td className="px-2 py-1.5 font-semibold">{dd.name}</td>
                      <td className="px-2 py-1.5 text-xs">{dd.frequency}</td>
                      <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(dd.due_date)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{dd.quantity_completed}/{dd.quantity_promised}</td>
                      <td className="px-2 py-1.5 text-xs">{dd.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </section>

          {/* Tasks */}
          <section>
            <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Work / Tasks — completed ({groupedTasks.completed.length}) & active ({groupedTasks.active.length})</h2>
            {d.tasks.length === 0 ? <div className="text-sm text-muted-foreground">No tasks yet.</div> :
              <table className="w-full text-sm">
                <thead className="bg-muted/40"><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                  <th className="px-2 py-1.5">Task</th><th className="px-2 py-1.5">Assignee</th><th className="px-2 py-1.5">Due</th><th className="px-2 py-1.5">Priority</th><th className="px-2 py-1.5">Status</th>
                </tr></thead>
                <tbody>
                  {d.tasks.map((t) => (
                    <tr key={t.id} className="border-t border-border">
                      <td className="px-2 py-1.5">
                        <div className="font-semibold">{t.name}</div>
                        {t.description && <div className="text-[10px] text-muted-foreground">{t.description}</div>}
                      </td>
                      <td className="px-2 py-1.5 text-xs">{userName(t.assignee_id)}</td>
                      <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(t.due_date)}</td>
                      <td className="px-2 py-1.5 text-xs">{t.priority}</td>
                      <td className="px-2 py-1.5 text-xs">{t.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            }
          </section>

          {/* Quotations */}
          {d.finance_visible && (
            <section>
              <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Quotations ({d.quotations.length})</h2>
              {d.quotations.length === 0 ? <div className="text-sm text-muted-foreground">No quotations issued.</div> :
                <table className="w-full text-sm">
                  <thead className="bg-muted/40"><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                    <th className="px-2 py-1.5">Quote #</th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Status</th><th className="px-2 py-1.5 text-right">Amount</th>
                  </tr></thead>
                  <tbody>
                    {d.quotations.map((q) => (
                      <tr key={q.id} className="border-t border-border">
                        <td className="px-2 py-1.5 font-mono font-bold">{q.quote_number}</td>
                        <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(q.date)}</td>
                        <td className="px-2 py-1.5 text-xs">{q.status}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{INR(q.final_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              }
            </section>
          )}

          {/* Payments */}
          {d.finance_visible && (
            <section>
              <h2 className="font-display font-extrabold text-lg border-b border-border pb-1 mb-2">Payments ({d.payments.length})</h2>
              {d.payments.length === 0 ? <div className="text-sm text-muted-foreground">No payments recorded.</div> :
                <table className="w-full text-sm">
                  <thead className="bg-muted/40"><tr className="text-left font-mono text-[10px] uppercase text-muted-foreground">
                    <th className="px-2 py-1.5">Payment ID</th><th className="px-2 py-1.5">Date</th><th className="px-2 py-1.5">Method</th><th className="px-2 py-1.5">Reference</th><th className="px-2 py-1.5 text-right">Amount</th>
                  </tr></thead>
                  <tbody>
                    {d.payments.map((p) => (
                      <tr key={p.id} className="border-t border-border">
                        <td className="px-2 py-1.5 font-mono text-xs">{p.payment_id}</td>
                        <td className="px-2 py-1.5 text-xs font-mono">{fmtDate(p.payment_date)}</td>
                        <td className="px-2 py-1.5 text-xs">{p.method}</td>
                        <td className="px-2 py-1.5 text-xs font-mono">{p.reference || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">{INR(p.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              }
            </section>
          )}

          <div className="text-center text-xs text-muted-foreground border-t border-border pt-4">
            End of report · Generated by Digitalis CRM · {fmtRelative(d.exported_at)}
          </div>
        </div>
      </div>
    </>
  );
}
